import { sanitizeHtml } from 'magical-trails-react';
import { PAGE_TYPES, type Page } from 'magical-trails';

/**
 * Turns a page's content into what a messenger would show. Authors write
 * pages as they always have (a title, an image, some rich text); here each
 * top-level paragraph becomes its own bubble, which is what makes a page of
 * prose read as someone typing rather than as an article in a speech bubble.
 */

export type SenderBubble =
  | { kind: 'html'; html: string; text: string }
  | { kind: 'image'; src: string; alt: string };

/** Sanitised HTML split into one chunk per top-level block. */
export function splitIntoBubbles(html: string | undefined): SenderBubble[] {
  const clean = sanitizeHtml(html ?? '').trim();
  if (!clean) return [];
  const doc = new DOMParser().parseFromString(
    `<body>${clean}</body>`,
    'text/html'
  );
  const bubbles: SenderBubble[] = [];
  doc.body.childNodes.forEach((node) => {
    const text = (node.textContent ?? '').replace(/\s+/g, ' ').trim();
    if (node.nodeType === Node.TEXT_NODE) {
      if (text) bubbles.push({ kind: 'html', html: escapeText(text), text });
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const element = node as Element;
    // An empty paragraph is the editor's blank line, not a message.
    if (!text && !element.querySelector('img')) return;
    if (element.tagName === 'HR') return;
    // A heading in a bubble would cut across the screen's own outline (one
    // h1 per screen: the sender's name). It stays bold, just not a heading.
    const html = /^H[1-6]$/.test(element.tagName)
      ? `<p><strong>${element.innerHTML}</strong></p>`
      : element.outerHTML;
    bubbles.push({ kind: 'html', html, text });
  });
  return bubbles;
}

function escapeText(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function image(src: string | undefined, alt: string | undefined) {
  return src ? [{ kind: 'image' as const, src, alt: alt ?? '' }] : [];
}

function titled(title: string | undefined): SenderBubble[] {
  const text = title?.trim();
  return text
    ? [{ kind: 'html', html: `<strong>${escapeText(text)}</strong>`, text }]
    : [];
}

/**
 * What the sender "says" for a page: everything for a text page, the ask for
 * a reply / photo page. Pages that open as a card say nothing themselves.
 */
export function senderBubblesFor(page: Page): SenderBubble[] {
  switch (page.type) {
    case 'pageLayout01':
    case 'pageLayout02':
      return [
        ...image(page.image, page.altText),
        ...titled(page.title),
        ...splitIntoBubbles(page.content),
      ];
    case 'audioPage':
      return [...titled(page.title), ...splitIntoBubbles(page.content)];
    case 'videoPage':
      return [...titled(page.title), ...splitIntoBubbles(page.caption)];
    case 'question':
      return [
        ...image(page.questionImage, ''),
        ...splitIntoBubbles(page.question),
      ];
    case 'visionMatch':
    case 'objectScan': {
      const asked = splitIntoBubbles(page.instructions);
      if (asked.length > 0) return asked;
      const hint = page.scannerHint?.trim() || page.title?.trim();
      return hint
        ? [{ kind: 'html', html: escapeText(hint), text: hint }]
        : [
            {
              kind: 'html',
              html: 'Send me a photo.',
              text: 'Send me a photo.',
            },
          ];
    }
    default:
      return [];
  }
}

/** One plain line for thread lists and banners. */
export function previewFor(page: Page | undefined): string {
  if (!page) return '';
  const spoken = senderBubblesFor(page).find(
    (bubble) => bubble.kind === 'html' && bubble.text
  );
  if (spoken && spoken.kind === 'html') return spoken.text;
  if (page.type === 'audioPage') return 'Voice message';
  if (page.type === 'videoPage') return 'Video';
  if ('image' in page && page.image) return 'Photo';
  return cardTitleFor(page);
}

/** What to call a page that shows as a card. */
export function cardTitleFor(page: Page): string {
  const title = 'title' in page ? page.title?.trim() : undefined;
  if (title) return title;
  return (
    PAGE_TYPES.find((option) => option.value === page.type)?.label ??
    'Attachment'
  );
}

export function cardKindLabel(page: Page): string {
  return (
    PAGE_TYPES.find((option) => option.value === page.type)?.label ?? 'Activity'
  );
}

/**
 * How long the sender "types" before a bubble lands. Long enough to read as
 * typing, short enough that nobody waits on a wall of text.
 */
export function typingDelayMs(bubble: SenderBubble, reducedMotion: boolean) {
  if (reducedMotion) return 150;
  if (bubble.kind === 'image') return 900;
  return Math.min(2200, Math.max(700, bubble.text.length * 22));
}
